import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

type MemberOption = {
  id: string;
  label: string;
  kind: "role" | "style";
};

type TemplateConfig = {
  id: string;
  title: string;
  description: string;
  members: string[];
};

const roleOptions: MemberOption[] = [
  { id: "mei", label: "メイ", kind: "role" },
  { id: "cto", label: "仮想CTO", kind: "role" },
  { id: "cfo", label: "仮想CFO", kind: "role" },
  { id: "coo", label: "仮想COO", kind: "role" },
  { id: "pm", label: "PM役", kind: "role" },
  { id: "ux", label: "ユーザー目線役", kind: "role" },
];

const styleOptions: MemberOption[] = [
  { id: "careful", label: "慎重派", kind: "style" },
  { id: "bold", label: "強気派", kind: "style" },
  { id: "realist", label: "現実派", kind: "style" },
  { id: "idea", label: "発想派", kind: "style" },
];

const templates: TemplateConfig[] = [
  {
    id: "management",
    title: "経営会議",
    description: "売上、投資、リスクを多角的に整理するための会議です。",
    members: ["mei", "cto", "cfo", "coo", "careful"],
  },
  {
    id: "project",
    title: "プロジェクト会議",
    description: "優先順位、進め方、体制を整理するための会議です。",
    members: ["mei", "pm", "cto", "ux", "realist"],
  },
  {
    id: "custom",
    title: "自由にメンバーを選ぶ",
    description: "役割や性格を組み合わせて、自分だけの相談チームを作ります。",
    members: ["mei"],
  },
];

function resolveMemberLabel(id: string) {
  return [...roleOptions, ...styleOptions].find((option) => option.id === id)?.label ?? id;
}

export default function GroupTalkHub() {
  const navigate = useNavigate();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(templates[0].id);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(templates[0].members);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? templates[0],
    [selectedTemplateId]
  );

  const selectTemplate = (template: TemplateConfig) => {
    setSelectedTemplateId(template.id);
    setSelectedMemberIds(template.members);
  };

  const toggleMember = (memberId: string) => {
    setSelectedMemberIds((current) => {
      if (memberId === "mei") return current;
      return current.includes(memberId) ? current.filter((id) => id !== memberId) : [...current, memberId];
    });
  };

  const openPreview = () => {
    navigate("/talk/group/preview", {
      state: {
        title: selectedTemplate.title,
        description: selectedTemplate.description,
        members: selectedMemberIds.map((id) => resolveMemberLabel(id)),
      },
    });
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-lg">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/talk" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← 戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">みんなで話す</p>
          <span className="text-xs text-[#9C867D]">Mock</span>
        </header>

        <section className="rounded-[2.4rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">Group Talk</p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-[#7C5A4E]">会議の型を選ぶ</h1>
          </div>

          <div className="mt-8 grid gap-4">
            {templates.map((template) => {
              const selected = template.id === selectedTemplateId;
              return (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => selectTemplate(template)}
                  className={`rounded-[1.8rem] border p-5 text-left transition ${
                    selected
                      ? "border-[#D1907B] bg-[#FFF7F1] shadow-sm"
                      : "border-[#EADBC8] bg-white hover:bg-[#FEFBF9]"
                  }`}
                >
                  <span className="inline-flex rounded-full bg-white px-3 py-1 text-[11px] font-black text-[#A46D4C]">
                    {template.id === "custom" ? "自由編成" : "テンプレート"}
                  </span>
                  <h2 className="mt-4 text-lg font-black text-[#5D4D46]">{template.title}</h2>
                  <p className="mt-2 text-sm leading-6 text-[#6B5851]">{template.description}</p>
                </button>
              );
            })}
          </div>

          <div className="mt-8 rounded-[2rem] border border-[#EADBC8] bg-[#FFFBF8] p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-black text-[#7C5A4E]">{selectedTemplate.title}</h2>
                <p className="mt-1 text-sm text-[#6B5851]">{selectedTemplate.description}</p>
              </div>
              <span className="rounded-full border border-[#E6DACD] bg-white px-3 py-1 text-[11px] font-black text-[#A46D4C]">
                モック
              </span>
            </div>

            <div className="mt-5">
              <p className="text-sm font-black text-[#5D4D46]">現在の参加メンバー</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedMemberIds.map((memberId) => (
                  <span
                    key={memberId}
                    className="rounded-full border border-[#D9C5B9] bg-white px-3 py-1.5 text-sm font-bold text-[#5D4D46]"
                  >
                    {resolveMemberLabel(memberId)}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-black text-[#5D4D46]">役割で追加する</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {roleOptions.map((option) => {
                  const selected = selectedMemberIds.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => toggleMember(option.id)}
                      disabled={option.id === "mei"}
                      className={`rounded-full border px-3 py-2 text-sm font-bold transition ${
                        selected
                          ? "border-[#D1907B] bg-[#FFF3EC] text-[#7C5A4E]"
                          : "border-[#EADBC8] bg-white text-[#6B5851] hover:bg-[#FEFBF9]"
                      } ${option.id === "mei" ? "cursor-default opacity-80" : ""}`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-black text-[#5D4D46]">視点や性格を追加する</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {styleOptions.map((option) => {
                  const selected = selectedMemberIds.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => toggleMember(option.id)}
                      className={`rounded-full border px-3 py-2 text-sm font-bold transition ${
                        selected
                          ? "border-[#D1907B] bg-[#FFF3EC] text-[#7C5A4E]"
                          : "border-[#EADBC8] bg-white text-[#6B5851] hover:bg-[#FEFBF9]"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Link
                to="/talk"
                className="rounded-full border border-[#EADBC8] bg-white px-5 py-3 text-center text-sm font-bold text-[#5D4D46]"
              >
                戻る
              </Link>
              <button
                type="button"
                onClick={openPreview}
                className="rounded-full bg-[#5D4D46] px-6 py-3 text-sm font-black text-white transition hover:bg-[#4A3E39]"
              >
                この内容を確認する
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
